/**
 * Outcome of a share attempt, so the caller can tell "the user changed their
 * mind" apart from "we failed".
 *
 * The old ShareButton could not: `navigator.share()` rejects with an
 * `AbortError` when the user dismisses the sheet, and the `catch` fell straight
 * through to `clipboard.writeText()` and flashed **"Link copied!"**. The buyer
 * said no and we did something anyway. The other end was worse: a clipboard
 * failure was swallowed with "nothing sensible left to do", so tapping Share
 * could do literally nothing with no feedback at all. (W13 R124)
 */
export type ShareOutcome = 'shared' | 'copied' | 'dismissed' | 'failed';

export interface SharePayload {
  title: string;
  url: string;
  /** One line of real listing detail, so a share is worth reading. */
  text?: string;
}

interface ShareDeps {
  share?: (data: ShareData) => Promise<void>;
  writeText?: (text: string) => Promise<void>;
}

/** True when a rejection means "the user dismissed the sheet", not "it broke". */
export function isAbort(err: unknown): boolean {
  if (err instanceof DOMException) return err.name === 'AbortError';
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * Share, then fall back to the clipboard — but only when the native sheet
 * actually failed. A dismissal is a decision and is reported as such.
 */
export async function shareOrCopy(
  payload: SharePayload,
  deps: ShareDeps = {}
): Promise<ShareOutcome> {
  const share =
    deps.share ??
    (typeof navigator !== 'undefined' && navigator.share
      ? navigator.share.bind(navigator)
      : undefined);
  const writeText =
    deps.writeText ??
    (typeof navigator !== 'undefined' && navigator.clipboard
      ? navigator.clipboard.writeText.bind(navigator.clipboard)
      : undefined);

  if (share) {
    try {
      await share({ title: payload.title, text: payload.text, url: payload.url });
      return 'shared';
    } catch (err) {
      if (isAbort(err)) return 'dismissed';
      // A real failure (no user gesture, unsupported payload) — copying is a
      // genuine second-best rather than an override of the buyer's choice.
    }
  }

  if (!writeText) return 'failed';
  try {
    await writeText(payload.url);
    return 'copied';
  } catch {
    return 'failed';
  }
}
